// The aspect chevrons (R2407; ui-map-interface.md §6.1.11, §6.3.2, §6.3.3):
// card 10 of mockup round 104, "Quarter Chevrons".
//
// A shot strikes the bow, a beam or the stern of its target, and the arc
// sets the damage: ×1 on the bow, ×1.25 on a beam, ×1.5 on the stern
// (numbers.md §4.1). One chevron is one quarter more damage, so a hull
// draws none on its bow, one on each beam and two astern, all pointing at
// the hull. The damage figure carries the same count after its digits, and
// an ambush puts the hollow diamond in front of them.
//
// This module holds every rule the three surfaces share: the count of an
// arc, the hulls that read the bow all round, the reach gate, the zoom
// gate, the ink, the side of a beam hit, and the drawings the atlas bakes.
// It keeps no state, and the frame functions allocate nothing (§10.4).

import {
  AnchorState, HitAspect, ShipClass, SignatureOrder,
} from '../gen/proto/aquaphobia';
import { FLEET_ASPECT } from '../gen/presentation';

/** How many chevrons the arc `a` carries: its quarters of extra damage. */
export function chevronCount(a: HitAspect): number {
  if (a === HitAspect.HIT_ASPECT_BEAM) return 1;
  if (a === HitAspect.HIT_ASPECT_STERN) return 2;
  return 0;
}

/**
 * The higher of two arcs, for a merged gun figure (R2407): the figure
 * shows the highest arc of its shots. The enum runs NONE, BOW, BEAM,
 * STERN, so the higher number is the higher arc.
 */
export function higherAspect(a: HitAspect, b: HitAspect): HitAspect {
  return a >= b ? a : b;
}

/** The fields of a fleet record that `readsBowAllRound` reads. */
export interface AllRoundFields {
  cls: ShipClass;
  anchor: AnchorState;
  signatureActive: boolean;
  signatureOrder: SignatureOrder;
}

/**
 * Does the hull read the bow aspect all round, so it draws no chevron?
 * The rule is `allRoundAspect` of the sim (numbers.md §4.1): a hull whose
 * anchor is SET and that stays where it anchors (a planted Sentinel, a
 * berthed Tender, a set Beacon: every fixture, R2858), and a hull under
 * DIG IN. The order state is public (R2406), so a hostile Basilisk under
 * DIG IN draws none either.
 */
export function readsBowAllRound(w: AllRoundFields): boolean {
  if (w.anchor === AnchorState.ANCHOR_STATE_SET) return true;
  return w.signatureActive && w.signatureOrder === SignatureOrder.SIGNATURE_ORDER_DIG_IN;
}

/**
 * The zoom gate: the chevrons draw while the screen scale of the hull
 * mark, the camera zoom times the mark scale (§6.1.9), is at least
 * `minScale`. Under it the two stern chevrons blur into one mark.
 */
export function chevronsReadAt(zoom: number, markScale: number): boolean {
  return zoom * markScale >= FLEET_ASPECT.minScale;
}

/**
 * The alpha the rest chevrons of one hull ease toward. The selected own
 * hull draws them faint at peace; every hull draws them full while a
 * hostile is in reach, and no other hull draws them at peace.
 */
export function chevronTarget(selectedOwn: boolean, threat: boolean): number {
  if (threat) return FLEET_ASPECT.threatAlpha;
  return selectedOwn ? FLEET_ASPECT.peaceAlpha : 0;
}

/**
 * The ink of the chevrons: the pale text ink for an own or a friendly
 * hull, and the faction ink of a hostile hull, so the reader sees where
 * its stern is.
 */
export function chevronInk(friendly: boolean, factionInk: number): number {
  return friendly ? FLEET_ASPECT.friendColor : factionInk;
}

/**
 * The reach gate of hull `i`: does a hull of another faction stand inside
 * the gun reach of the one or the other? `reach[j]` is the gun reach of
 * hull `j` in world px, or a negative figure for a hull the gate must
 * skip (a contact that fades out). A hull of an unknown class carries the
 * widest reach of the catalog, which the caller writes. The test is the
 * squared distance against the squared larger reach, so it takes no
 * square root, and it stops at the first hostile it finds.
 */
export function inHostileReach(
  i: number, n: number,
  x: ArrayLike<number>, y: ArrayLike<number>,
  faction: ArrayLike<number>, reach: ArrayLike<number>,
): boolean {
  const ri = reach[i];
  if (ri < 0) return false;
  const fi = faction[i], xi = x[i], yi = y[i];
  for (let j = 0; j < n; j++) {
    if (j === i || faction[j] === fi || reach[j] < 0) continue;
    const r = ri > reach[j] ? ri : reach[j];
    if (r <= 0) continue;
    const dx = x[j] - xi, dy = y[j] - yi;
    if (dx * dx + dy * dy <= r * r) return true;
  }
  return false;
}

/**
 * The side of a beam hit, in the frame of the hull: +1 when the shooter
 * stands on the side of +y with the bow along +x (the sine of the bearing
 * from the bow is positive), else −1. `bow` is the drawn heading.
 */
export function beamSide(bow: number, hx: number, hy: number, sx: number, sy: number): 1 | -1 {
  const dx = sx - hx, dy = sy - hy;
  return Math.cos(bow) * dy - Math.sin(bow) * dx >= 0 ? 1 : -1;
}

/**
 * The bearing from the bow of the centre of the struck arc: π for the
 * stern, ±π/2 for a beam (by `side`), and NaN for an arc with no chevron.
 */
export function arcCentre(a: HitAspect, side: 1 | -1): number {
  if (a === HitAspect.HIT_ASPECT_STERN) return Math.PI;
  if (a === HitAspect.HIT_ASPECT_BEAM) return side * Math.PI / 2;
  return Number.NaN;
}

// ---------------------------------------------------------------------------
// The cells
// ---------------------------------------------------------------------------

/**
 * Which part of the set a rest cell leaves out: nothing, the stern pair,
 * or the beam chevron of one side. A hull whose arc flashes draws the cell
 * without that arc, and the flash draws the arc itself (§6.3.3).
 */
export type ChevronGap = 'none' | 'stern' | 'side+' | 'side-';
export const CHEVRON_GAPS: readonly ChevronGap[] = ['none', 'stern', 'side+', 'side-'];

/** The atlas key of the rest set with one gap. */
export const chevronSetKey = (gap: ChevronGap) => `aspect:set:${gap}`;
/** The atlas key of one chevron at the rest stroke and at the flash stroke. */
export const CHEVRON_KEY = 'aspect:chev';
export const CHEVRON_FLASH_KEY = 'aspect:flash';

/** The gap a flash of arc `a` on side `side` opens in the rest set. */
export function gapOf(a: HitAspect, side: 1 | -1): ChevronGap {
  if (a === HitAspect.HIT_ASPECT_STERN) return 'stern';
  if (a === HitAspect.HIT_ASPECT_BEAM) return side > 0 ? 'side+' : 'side-';
  return 'none';
}

/**
 * The tip radius of the first chevron of arc `a`: `innerR` on a beam and
 * `sternR` astern, because the stern pair stands outside the charge pip
 * of a hostile hull (§6.1.8, R2417).
 */
export function firstChevronR(a: HitAspect): number {
  return a === HitAspect.HIT_ASPECT_STERN ? FLEET_ASPECT.sternR : FLEET_ASPECT.innerR;
}

/**
 * The outer edge of the stern pair: the radius at which the arms of the
 * second stern chevron end, plus half the stroke. A mark astern of a hull
 * stands past it (R2417).
 */
export function sternSetEdge(): number {
  const a = FLEET_ASPECT;
  return a.sternR + a.stepR + a.arm * Math.cos(a.spread) + a.width / 2;
}

/** The half side of the square box of the rest set, in world px. */
export function chevronSetHalf(): number {
  const a = FLEET_ASPECT;
  return Math.max(a.innerR, a.sternR + a.stepR) + a.arm + a.width;
}

/** The half side of the square box of one chevron, centred on its tip. */
export function chevronHalf(width: number): number {
  return FLEET_ASPECT.arm + width;
}

/**
 * Strokes one chevron whose tip is at (x, y) and points along `ang`: two
 * arms of length `arm`, each `spread` off the line back from the tip.
 */
function strokeChevron(
  ctx: CanvasRenderingContext2D, x: number, y: number, ang: number,
  arm: number, spread: number, width: number,
): void {
  const b1 = ang + Math.PI - spread, b2 = ang + Math.PI + spread;
  ctx.beginPath();
  ctx.moveTo(x + Math.cos(b1) * arm, y + Math.sin(b1) * arm);
  ctx.lineTo(x, y);
  ctx.lineTo(x + Math.cos(b2) * arm, y + Math.sin(b2) * arm);
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke();
}

/**
 * The rest set, white, with the bow along +x: one chevron on each beam at
 * `innerR` and two astern at `sternR` and `sternR + stepR`, every one
 * pointing at the hull. `gap` leaves one arc out.
 */
export function drawChevronSet(ctx: CanvasRenderingContext2D, gap: ChevronGap): void {
  const a = FLEET_ASPECT;
  ctx.strokeStyle = '#ffffff';
  const one = (bearing: number, r: number) => strokeChevron(
    ctx, Math.cos(bearing) * r, Math.sin(bearing) * r, bearing + Math.PI, a.arm, a.spread, a.width);
  if (gap !== 'side+') one(Math.PI / 2, a.innerR);
  if (gap !== 'side-') one(-Math.PI / 2, a.innerR);
  if (gap !== 'stern') {
    one(Math.PI, a.sternR);
    one(Math.PI, a.sternR + a.stepR);
  }
}

/** One white chevron, its tip on the origin and pointing along +x. */
export function drawOneChevron(ctx: CanvasRenderingContext2D, width: number): void {
  const a = FLEET_ASPECT;
  ctx.strokeStyle = '#ffffff';
  strokeChevron(ctx, 0, 0, 0, a.arm, a.spread, width);
}

/**
 * The chevron of the damage figure's suffix at figure size `px`: the tip
 * points along +x, and the mark is centred on the origin, so the layer
 * places it by its advance alone. The stroke colour is the caller's.
 */
export function drawFigureChevron(ctx: CanvasRenderingContext2D, px: number, width: number): void {
  const a = FLEET_ASPECT;
  const arm = a.figureArmEm * px;
  strokeChevron(ctx, arm * Math.cos(a.spread) / 2, 0, 0, arm, a.spread, width);
}

/**
 * The hollow diamond of an ambush at figure size `px`, centred on the
 * origin: half its height is `figureDiamondEm` of the size, and half its
 * width three quarters of that, as the card draws it.
 */
export function drawFigureDiamond(ctx: CanvasRenderingContext2D, px: number, width: number): void {
  const s = FLEET_ASPECT.figureDiamondEm * px;
  ctx.beginPath();
  ctx.moveTo(0, -s);
  ctx.lineTo(s * 0.75, 0);
  ctx.lineTo(0, s);
  ctx.lineTo(-s * 0.75, 0);
  ctx.closePath();
  ctx.lineWidth = width;
  ctx.lineJoin = 'round';
  ctx.stroke();
}
